/**
 * Password Utility - Based on NIST Digital Identity Guidelines (2024)
 *
 * NIST recommends:
 * - Minimum length of 12 characters (preferably 15+)
 * - Encourage passphrases over complex character requirements
 * - No mandatory special characters, uppercase, or numbers
 * - Check against common/compromised passwords
 * - No arbitrary complexity rules that encourage weak patterns
 *
 * Example of a strong passphrase: "coffee-morning-sunshine-blue"
 * This is easier to remember and more secure than: "P@ssw0rd1"
 */

// Common weak passwords and patterns to reject
const COMMON_WEAK_PASSWORDS = [
  "password",
  "password1",
  "password123",
  "12345678",
  "123456789",
  "qwerty",
  "abc123",
  "monkey",
  "letmein",
  "trustno1",
  "dragon",
  "baseball",
  "iloveyou",
  "master",
  "sunshine",
  "ashley",
  "bailey",
  "passw0rd",
  "shadow",
  "superman",
  "qazwsx",
  "michael",
  "football",
  "welcome",
  "jesus",
];

// Sequential patterns to detect
const SEQUENTIAL_PATTERNS = [
  "12345",
  "23456",
  "34567",
  "45678",
  "56789",
  "abcde",
  "bcdef",
  "cdefg",
  "defgh",
  "efghi",
];

/**
 * Validates password based on NIST guidelines
 * @param {string} password - The password to validate
 * @returns {Object} - { isValid: boolean, message: string, strength: string }
 */
export const validatePassword = (password) => {
  // Check if password exists
  if (!password || password.trim() === "") {
    return {
      isValid: false,
      message: "Password is required.",
      strength: "none",
    };
  }

  const trimmedPassword = password.trim();
  const length = trimmedPassword.length;

  // NIST Guideline: Minimum 12 characters (we use 12 as minimum, 15+ recommended)
  if (length < 12) {
    return {
      isValid: false,
      message:
        'Password must be at least 12 characters long. Try a memorable passphrase like "coffee-morning-sunshine-blue".',
      strength: "weak",
    };
  }

  // Check for very long passwords (set reasonable upper limit for database storage)
  if (length > 30) {
    return {
      isValid: false,
      message: "Password cannot exceed 30 characters.",
      strength: "none",
    };
  }

  // Check against common weak passwords (case-insensitive)
  const lowerPassword = trimmedPassword.toLowerCase();
  if (COMMON_WEAK_PASSWORDS.includes(lowerPassword)) {
    return {
      isValid: false,
      message:
        "This password is too common. Please choose a more unique passphrase.",
      strength: "weak",
    };
  }

  // Check for sequential patterns
  for (const pattern of SEQUENTIAL_PATTERNS) {
    if (lowerPassword.includes(pattern)) {
      return {
        isValid: false,
        message:
          "Password contains sequential characters. Try a more random passphrase.",
        strength: "weak",
      };
    }
  }

  // Check for repeated characters (e.g., "aaaaaaa" or "1111111")
  if (/(.)\1{5,}/.test(trimmedPassword)) {
    return {
      isValid: false,
      message:
        "Password contains too many repeated characters. Create a more varied passphrase.",
      strength: "weak",
    };
  }

  // Check for all same character type (all numbers or all letters)
  if (/^\d+$/.test(trimmedPassword)) {
    return {
      isValid: false,
      message:
        "Password cannot contain only numbers. Try mixing words and numbers.",
      strength: "weak",
    };
  }

  // Determine strength based on length and character variety
  let strength = "good";

  if (length >= 20) {
    strength = "excellent";
  } else if (length >= 15) {
    strength = "strong";
  }

  // Bonus: Check for character variety (not required, but improves strength rating)
  const hasLowercase = /[a-z]/.test(trimmedPassword);
  const hasUppercase = /[A-Z]/.test(trimmedPassword);
  const hasNumber = /\d/.test(trimmedPassword);
  const hasSpecial = /[^a-zA-Z0-9]/.test(trimmedPassword);

  const varietyCount = [
    hasLowercase,
    hasUppercase,
    hasNumber,
    hasSpecial,
  ].filter(Boolean).length;

  if (length >= 15 && varietyCount >= 3) {
    strength = "excellent";
  } else if (length >= 12 && varietyCount >= 2) {
    strength = "strong";
  }

  return {
    isValid: true,
    message: "Password meets security requirements.",
    strength: strength,
  };
};

/**
 * Gets a user-friendly message for password strength
 * @param {string} strength - The strength level
 * @returns {string} - Descriptive message
 */
export const getStrengthMessage = (strength) => {
  switch (strength) {
    case "excellent":
      return "✓ Excellent password strength!";
    case "strong":
      return "✓ Strong password";
    case "good":
      return "✓ Good password (consider making it longer)";
    case "weak":
      return "⚠ Weak password";
    default:
      return "";
  }
};

/**
 * Gets color for password strength indicator
 * @param {string} strength - The strength level
 * @returns {string} - Color code
 */
export const getStrengthColor = (strength) => {
  switch (strength) {
    case "excellent":
      return "#28a745"; // Green
    case "strong":
      return "#5cb85c"; // Light green
    case "good":
      return "#f0ad4e"; // Orange
    case "weak":
      return "#d9534f"; // Red
    default:
      return "#6c757d"; // Gray
  }
};

/**
 * Provides helpful examples of strong passphrases
 * @returns {string[]} - Array of example passphrases
 */
export const getPassphraseExamples = () => {
  return [
    "coffee-morning-sunshine-blue",
    "pizza-delivers-happiness-always",
    "mountain-hiking-adventure-2024",
    "books-library-reading-comfort",
    "ocean-waves-beach-sunset",
  ];
};

/**
 * Generates password requirements text for display
 * @returns {string} - Requirements description
 */
export const getPasswordRequirements = () => {
  return `Password Requirements:
• Minimum 12 characters (15+ recommended for better security)
• Use a memorable passphrase instead of complex characters
• Avoid common words, sequential patterns, or repeated characters
• Example: "coffee-morning-sunshine-blue"

Strong passphrases are easier to remember and more secure than complex short passwords!`;
};

/**
 * Checks if confirm password matches password character by character up to current keystroke
 * @param {string} password - The original password
 * @param {string} confirmPassword - The confirmation password being typed
 * @returns {Object} - { hasError: boolean, isComplete: boolean }
 *   - hasError: true if characters don't match up to current keystroke
 *   - isComplete: true if passwords match completely
 */
export const checkPasswordMatch = (password, confirmPassword) => {
  // Empty confirm password is not an error
  if (confirmPassword === "") {
    return { hasError: false, isComplete: false };
  }

  // Check if confirm password matches password character by character
  const matches = password.startsWith(confirmPassword);
  const isComplete = confirmPassword === password && confirmPassword.length > 0;

  return {
    hasError: !matches,
    isComplete: isComplete,
  };
};

export default {
  validatePassword,
  getStrengthMessage,
  getStrengthColor,
  getPassphraseExamples,
  getPasswordRequirements,
  checkPasswordMatch,
};
